type ChartLikeTool = {
  toolName?: string;
  args?: unknown;
  toolCallId?: string;
};

const SEO_AUTO_CHART_ID_PREFIX = 'seo_chart_';

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function unwrapPresentChartArgs(args: unknown): unknown {
  const rec = asRecord(args);
  if (!rec) return args;
  const nested =
    asRecord(rec.input) ?? asRecord(rec.args) ?? asRecord(rec.parameters);
  if (
    nested &&
    (typeof nested.xKey === 'string' ||
      Array.isArray(nested.dataKeys) ||
      Array.isArray(nested.data))
  ) {
    return nested;
  }
  return rec;
}

function presentChartTitleKey(args: unknown): string | null {
  const rec = asRecord(unwrapPresentChartArgs(args));
  if (!rec) return null;
  const title = typeof rec.title === 'string' ? rec.title.trim() : '';
  return title || null;
}

function isBarLikePresentChart(args: unknown): boolean {
  const rec = asRecord(unwrapPresentChartArgs(args));
  if (!rec) return true;
  const type = typeof rec.type === 'string' ? rec.type.trim() : '';
  return type === '' || type === 'bar';
}

function isSeoAutoChartId(toolCallId: unknown): boolean {
  return String(toolCallId ?? '').startsWith(SEO_AUTO_CHART_ID_PREFIX);
}

/** Same-data present_chart payloads (title ignored). */
export function presentChartDataKey(args: unknown): string | null {
  const rec = asRecord(unwrapPresentChartArgs(args));
  if (!rec) return null;
  const xKey = typeof rec.xKey === 'string' ? rec.xKey : '';
  const dataKeys = Array.isArray(rec.dataKeys)
    ? rec.dataKeys.map(key => String(key))
    : [];
  const data = Array.isArray(rec.data) ? rec.data : [];
  if (!xKey || dataKeys.length === 0 || data.length === 0) return null;

  const rows = data
    .map(row => {
      const item = asRecord(row);
      const label = item ? String(item[xKey] ?? '').trim() : '';
      const values = dataKeys.map(key => {
        const n = Number(item?.[key] ?? 0);
        return Number.isFinite(n) ? n : 0;
      });
      return { label, values };
    })
    .filter(row => row.label)
    .sort((a, b) => a.label.localeCompare(b.label, 'fa'));

  if (rows.length === 0) return null;
  return JSON.stringify({
    type: typeof rec.type === 'string' && rec.type.trim() ? rec.type : 'bar',
    rows
  });
}

function preferPresentChartIndex<T extends ChartLikeTool>(
  tools: T[],
  current: number,
  previous: number
): number {
  const currentAuto = isSeoAutoChartId(tools[current]?.toolCallId);
  const previousAuto = isSeoAutoChartId(tools[previous]?.toolCallId);
  if (currentAuto && !previousAuto) return current;
  if (previousAuto && !currentAuto) return previous;
  return current;
}

/** Keep one present_chart when several share bars/title; prefer auto-inject. */
export function dedupePresentChartTools<T extends ChartLikeTool>(
  tools: T[]
): T[] {
  const chartIndices: number[] = [];
  tools.forEach((tool, index) => {
    if (tool.toolName === 'present_chart') chartIndices.push(index);
  });
  if (chartIndices.length < 2) return tools;

  const drop = new Set<number>();
  const lastIndexByKey = new Map<string, number>();

  const considerKey = (key: string | null, index: number) => {
    if (!key) return;
    const previous = lastIndexByKey.get(key);
    if (previous === undefined) {
      lastIndexByKey.set(key, index);
      return;
    }
    const keep = preferPresentChartIndex(tools, index, previous);
    drop.add(keep === index ? previous : index);
    lastIndexByKey.set(key, keep);
  };

  chartIndices.forEach(index => {
    const args = tools[index]?.args;
    considerKey(presentChartDataKey(args), index);
    const title = presentChartTitleKey(args);
    if (title) considerKey(`title:${title}`, index);
  });

  const autoBar = chartIndices.filter(
    index =>
      isSeoAutoChartId(tools[index]?.toolCallId) &&
      isBarLikePresentChart(tools[index]?.args)
  );
  if (autoBar.length > 0) {
    const keepAuto = autoBar[autoBar.length - 1]!;
    drop.delete(keepAuto);
    chartIndices.forEach(index => {
      if (index !== keepAuto && isBarLikePresentChart(tools[index]?.args)) {
        drop.add(index);
      }
    });
  }

  if (drop.size === 0) return tools;
  return tools.filter((_, index) => !drop.has(index));
}
